/**
 * SpacingEditor — Advanced Visual Box Model Editor
 *
 * Chrome DevTools-style box model with:
 * - Nested rectangles: margin (orange) > padding (green) > content (blue)
 * - Individual side inputs with arrow key support
 * - Linked sides toggle (change all sides at once)
 * - Quick preset values
 * - Keyboard shortcuts (arrow keys ±1, shift+arrow ±10)
 * - Clear/reset per section
 */

import React, { useState, useCallback } from 'react';
import { Link2, Unlink } from 'lucide-react';

// ── Parse incoming value to plain number string ──
function parseValue(val) {
  if (val === null || val === undefined || val === '') return '';
  if (typeof val === 'number') return String(val);
  const str = String(val).trim();
  const num = parseFloat(str);
  if (isNaN(num)) return '';
  return String(num);
}

// ── Preset spacing values ──
const PRESETS = [0, 4, 8, 12, 16, 24, 32, 48, 64];

// ── Side Input ──
function SideInput({ value, onChange, side, type }) {
  const [localValue, setLocalValue] = useState(parseValue(value));
  const [focused, setFocused] = useState(false);

  React.useEffect(() => {
    if (!focused) setLocalValue(parseValue(value));
  }, [value, focused]);

  const commit = useCallback(() => {
    const trimmed = localValue.trim();
    if (trimmed === '') {
      onChange(side, '0px');
    } else {
      const num = parseFloat(trimmed);
      if (!isNaN(num)) onChange(side, `${num}px`);
    }
  }, [localValue, onChange, side]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') e.target.blur();
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      const current = parseFloat(localValue) || 0;
      const step = e.shiftKey ? 10 : 1;
      const next = e.key === 'ArrowUp' ? current + step : Math.max(0, current - step);
      setLocalValue(String(next));
      onChange(side, `${next}px`);
    }
  }, [localValue, onChange, side]);

  const isMargin = type === 'margin';
  const inputStyle = {
    width: 38, height: 20,
    border: focused ? `1px solid ${isMargin ? '#f59e0b' : '#22c55e'}` : '1px solid transparent',
    borderRadius: 3,
    background: focused ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.5)',
    textAlign: 'center', fontSize: 11,
    fontFamily: "'SF Mono','Fira Code','Consolas',monospace",
    color: '#374151', outline: 'none', padding: '1px 2px', lineHeight: '18px',
    transition: 'all 0.15s ease', cursor: 'text',
  };

  return (
    <input
      type="text"
      value={localValue}
      onChange={(e) => setLocalValue(e.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={() => { setFocused(false); commit(); }}
      onKeyDown={handleKeyDown}
      style={inputStyle}
      title={`${type} ${side}`}
    />
  );
}

// ── Main Component ──
export default function SpacingEditor({
  padding = {}, margin = {},
  onPaddingChange, onMarginChange,
}) {
  const [paddingLinked, setPaddingLinked] = useState(false);
  const [marginLinked, setMarginLinked] = useState(false);
  const [showPresets, setShowPresets] = useState(null); // null | 'padding' | 'margin'

  const p = { top: padding.top ?? 0, right: padding.right ?? 0, bottom: padding.bottom ?? 0, left: padding.left ?? 0 };
  const m = { top: margin.top ?? 0, right: margin.right ?? 0, bottom: margin.bottom ?? 0, left: margin.left ?? 0 };

  // Linked change: update all 4 sides at once
  const handleLinkedPaddingChange = useCallback((side, value) => {
    if (paddingLinked) {
      onPaddingChange('top', value);
      onPaddingChange('right', value);
      onPaddingChange('bottom', value);
      onPaddingChange('left', value);
    } else {
      onPaddingChange(side, value);
    }
  }, [paddingLinked, onPaddingChange]);

  const handleLinkedMarginChange = useCallback((side, value) => {
    if (marginLinked) {
      onMarginChange('top', value);
      onMarginChange('right', value);
      onMarginChange('bottom', value);
      onMarginChange('left', value);
    } else {
      onMarginChange(side, value);
    }
  }, [marginLinked, onMarginChange]);

  const applyPreset = useCallback((type, val) => {
    const v = `${val}px`;
    if (type === 'padding') {
      onPaddingChange('top', v); onPaddingChange('right', v);
      onPaddingChange('bottom', v); onPaddingChange('left', v);
    } else {
      onMarginChange('top', v); onMarginChange('right', v);
      onMarginChange('bottom', v); onMarginChange('left', v);
    }
    setShowPresets(null);
  }, [onPaddingChange, onMarginChange]);

  const containerStyle = { width: 280, userSelect: 'none', fontFamily: "'Inter',-apple-system,BlinkMacSystemFont,sans-serif" };

  const marginBoxStyle = {
    position: 'relative', background: 'rgba(251,191,36,0.08)',
    border: '1px dashed rgba(217,119,6,0.3)', borderRadius: 6, padding: '22px 18px',
  };

  const paddingBoxStyle = {
    position: 'relative', background: 'rgba(74,222,128,0.08)',
    border: '1px dashed rgba(22,163,74,0.3)', borderRadius: 4, padding: '22px 16px',
  };

  const contentBoxStyle = {
    background: 'rgba(96,165,250,0.1)', border: '1px solid rgba(59,130,246,0.25)',
    borderRadius: 3, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
  };

  const labelStyle = (color) => ({
    position: 'absolute', top: 4, left: 8,
    fontSize: 9, fontWeight: 700, letterSpacing: '0.08em',
    color, textTransform: 'uppercase', pointerEvents: 'none',
  });

  const linkBtnStyle = (isLinked, color) => ({
    position: 'absolute', top: 4, right: 6,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 18, height: 18, borderRadius: 4,
    border: 'none', cursor: 'pointer', padding: 0,
    background: isLinked ? color : 'transparent',
    color: isLinked ? '#fff' : 'rgba(0,0,0,0.2)',
    transition: 'all 0.15s',
  });

  const horizontalRowStyle = { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 };
  const verticalCenterStyle = { display: 'flex', justifyContent: 'center', padding: '3px 0' };

  return (
    <div style={containerStyle}>
      {/* Margin box */}
      <div style={marginBoxStyle}>
        <span style={labelStyle('rgba(180,83,9,0.55)')}>margin</span>

        {/* Link toggle for margin */}
        <button
          onClick={() => setMarginLinked(!marginLinked)}
          style={linkBtnStyle(marginLinked, 'rgba(217,119,6,0.6)')}
          title={marginLinked ? 'Unlink sides' : 'Link all sides'}
        >
          {marginLinked ? <Link2 size={10} /> : <Unlink size={10} />}
        </button>

        {/* Preset trigger */}
        <button
          onClick={() => setShowPresets(showPresets === 'margin' ? null : 'margin')}
          style={{
            position: 'absolute', top: 4, right: 28,
            fontSize: 8, fontWeight: 700, color: 'rgba(180,83,9,0.4)',
            background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px',
          }}
          title="Quick presets"
        >
          ···
        </button>

        {showPresets === 'margin' && (
          <div style={{
            position: 'absolute', top: 22, right: 6, zIndex: 5,
            background: '#fff', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            border: '1px solid #e5e7eb', padding: 6, display: 'flex', gap: 3, flexWrap: 'wrap', width: 160,
          }}>
            {PRESETS.map(v => (
              <button key={v} onClick={() => applyPreset('margin', v)}
                style={{
                  padding: '3px 7px', borderRadius: 4, border: '1px solid #e5e7eb',
                  background: '#fff', fontSize: 10, color: '#6b7280', cursor: 'pointer',
                  fontFamily: 'monospace', transition: 'all 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#f59e0b'; e.currentTarget.style.color = '#b45309'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.color = '#6b7280'; }}
              >{v}</button>
            ))}
          </div>
        )}

        <div style={verticalCenterStyle}>
          <SideInput value={m.top} onChange={handleLinkedMarginChange} side="top" type="margin" />
        </div>

        <div style={horizontalRowStyle}>
          <SideInput value={m.left} onChange={handleLinkedMarginChange} side="left" type="margin" />

          {/* Padding box */}
          <div style={{ ...paddingBoxStyle, flex: 1 }}>
            <span style={labelStyle('rgba(22,101,52,0.5)')}>padding</span>

            <button
              onClick={() => setPaddingLinked(!paddingLinked)}
              style={linkBtnStyle(paddingLinked, 'rgba(22,163,74,0.6)')}
              title={paddingLinked ? 'Unlink sides' : 'Link all sides'}
            >
              {paddingLinked ? <Link2 size={10} /> : <Unlink size={10} />}
            </button>

            <button
              onClick={() => setShowPresets(showPresets === 'padding' ? null : 'padding')}
              style={{
                position: 'absolute', top: 4, right: 28,
                fontSize: 8, fontWeight: 700, color: 'rgba(22,101,52,0.4)',
                background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px',
              }}
              title="Quick presets"
            >
              ···
            </button>

            {showPresets === 'padding' && (
              <div style={{
                position: 'absolute', top: 22, right: 6, zIndex: 5,
                background: '#fff', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                border: '1px solid #e5e7eb', padding: 6, display: 'flex', gap: 3, flexWrap: 'wrap', width: 160,
              }}>
                {PRESETS.map(v => (
                  <button key={v} onClick={() => applyPreset('padding', v)}
                    style={{
                      padding: '3px 7px', borderRadius: 4, border: '1px solid #e5e7eb',
                      background: '#fff', fontSize: 10, color: '#6b7280', cursor: 'pointer',
                      fontFamily: 'monospace', transition: 'all 0.15s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#22c55e'; e.currentTarget.style.color = '#166534'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.color = '#6b7280'; }}
                  >{v}</button>
                ))}
              </div>
            )}

            <div style={verticalCenterStyle}>
              <SideInput value={p.top} onChange={handleLinkedPaddingChange} side="top" type="padding" />
            </div>

            <div style={horizontalRowStyle}>
              <SideInput value={p.left} onChange={handleLinkedPaddingChange} side="left" type="padding" />
              <div style={{ ...contentBoxStyle, flex: 1 }}>
                <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.06em', color: 'rgba(37,99,235,0.5)', textTransform: 'uppercase', pointerEvents: 'none' }}>
                  content
                </span>
              </div>
              <SideInput value={p.right} onChange={handleLinkedPaddingChange} side="right" type="padding" />
            </div>

            <div style={verticalCenterStyle}>
              <SideInput value={p.bottom} onChange={handleLinkedPaddingChange} side="bottom" type="padding" />
            </div>
          </div>

          <SideInput value={m.right} onChange={handleLinkedMarginChange} side="right" type="margin" />
        </div>

        <div style={verticalCenterStyle}>
          <SideInput value={m.bottom} onChange={handleLinkedMarginChange} side="bottom" type="margin" />
        </div>
      </div>

      {/* Keyboard shortcut hint */}
      <div style={{
        marginTop: 8, fontSize: 9, color: '#b0b5bd', textAlign: 'center',
        letterSpacing: '0.02em',
      }}>
        Arrow keys ±1 · Shift+Arrow ±10
      </div>
    </div>
  );
}
